import { useEffect, useState } from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from '@tiptap/react';
import { useResolvedTheme } from '../use-theme';

export const MERMAID_DIAGRAM_NAME = 'mermaidDiagram';

type MermaidModule = typeof import('mermaid').default;
type Theme = 'light' | 'dark';

let mermaidModule: Promise<MermaidModule> | null = null;
let renderCounter = 0;

/** Mermaid is ~2 MB, so it is loaded on first use only. */
function loadMermaid(): Promise<MermaidModule> {
  if (!mermaidModule) {
    mermaidModule = import('mermaid').then((mod) => mod.default);
  }
  return mermaidModule;
}

/** Render Mermaid source to sanitised SVG markup; throws on syntax errors. */
export async function renderMermaid(source: string, theme: Theme): Promise<string> {
  const mermaid = await loadMermaid();
  mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: theme === 'dark' ? 'dark' : 'default' });
  renderCounter += 1;
  const { svg } = await mermaid.render(`wiki-mermaid-${renderCounter}`, source);
  return svg;
}

type RenderState = { status: 'loading' } | { status: 'ok'; svg: string } | { status: 'error'; message: string };

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message.split('\n')[0] ?? 'Invalid diagram';
  return 'Invalid diagram';
}

function DiagramView({ node }: ReactNodeViewProps) {
  const source = typeof node.attrs.source === 'string' ? node.attrs.source : '';
  const theme = useResolvedTheme();
  const [state, setState] = useState<RenderState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (source.trim().length === 0) {
      setState({ status: 'error', message: 'Empty diagram' });
      return;
    }
    renderMermaid(source, theme)
      .then((svg) => {
        if (!cancelled) setState({ status: 'ok', svg });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: 'error', message: errorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, theme]);

  return (
    <NodeViewWrapper data-mermaid-diagram="" className="my-3 rounded-md border bg-background">
      <div className="px-4 py-3" contentEditable={false}>
        {state.status === 'loading' ? (
          <p className="text-center text-xs text-muted-foreground">Rendering diagram…</p>
        ) : state.status === 'error' ? (
          <p className="text-center text-xs text-destructive" role="alert">
            Mermaid: {state.message}
          </p>
        ) : (
          <div className="mermaid-diagram w-full" dangerouslySetInnerHTML={{ __html: state.svg }} />
        )}
      </div>
    </NodeViewWrapper>
  );
}

/**
 * A ```mermaid fenced block rendered as a diagram. The Markdown stays a plain fence, so Obsidian
 * and any other Markdown tool render the same source.
 */
export const MermaidDiagram = Node.create({
  name: MERMAID_DIAGRAM_NAME,
  group: 'block',
  atom: true,
  selectable: true,
  // Above the code block (100) so this handler sees `code` tokens first; below Paragraph (1000).
  priority: 110,
  markdownTokenName: 'code',

  addAttributes() {
    return {
      source: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-mermaid-source') ?? element.textContent ?? '',
        renderHTML: (attrs) => ({ 'data-mermaid-source': attrs.source }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-mermaid-source]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const source = typeof node.attrs.source === 'string' ? node.attrs.source : '';
    return ['div', mergeAttributes(HTMLAttributes), ['pre', {}, ['code', { class: 'language-mermaid' }, source]]];
  },

  parseMarkdown(token, helpers) {
    // Any other fenced block falls through to the code block extension.
    if (token.lang !== 'mermaid') return [];
    return helpers.createNode(MERMAID_DIAGRAM_NAME, { source: typeof token.text === 'string' ? token.text : '' });
  },

  addNodeView() {
    return ReactNodeViewRenderer(DiagramView);
  },
});
